import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { SESSION_KEYS } from '@/config/constants';
import { termsApi } from '@/services/api';
import { metaAdsApi } from '@/services/api/metaAds';
import { useAuthStore } from '@/store/authStore';
import { useLinkageEligibilityQuery } from '@/hooks/queries/useLinkageEligibilityQuery';
import { debug } from '@/utils/debug';

interface LinkAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLinkAccount?: () => void;
}

export function LinkAccountModal({ isOpen, onClose, onLinkAccount }: LinkAccountModalProps) {
  const navigate = useNavigate();
  const hasLinkedGoogleAccounts = useAuthStore((s) => s.hasLinkedGoogleAccounts);
  const hasLinkedMetaAccounts = useAuthStore((s) => s.hasLinkedMetaAccounts);
  const {
    data: eligibility,
    isLoading: isEligibilityLoading,
    isError: isEligibilityError,
    refetch: refetchEligibility,
  } = useLinkageEligibilityQuery();
  // Eligibility is the source of truth (matches the server-side gates
  // on /google-ads/link-account and /auth/meta/authorize). We also
  // hide a button when the platform is already linked, so the modal
  // never surfaces a re-link CTA for something the user has.
  const showGoogleButton =
    (eligibility?.can_link_google ?? false) && !hasLinkedGoogleAccounts;
  const showMetaButton =
    (eligibility?.can_link_meta ?? false) && !hasLinkedMetaAccounts;
  // Upsell renders whenever the backend says `upgrade_required` and any
  // platform's button is missing — covers both the normal free-tier lock
  // and the orphan-credentials edge case where an auto-deactivated
  // credential row still trips `has_linked_platform` on the backend
  // while the frontend thinks nothing's linked (see plan doc
  // §"Design decisions" #2).
  const showUpgradeNudge =
    (eligibility?.upgrade_required ?? false) &&
    (!showGoogleButton || !showMetaButton);
  // Copy sourced from backend `can_link_*` (not local `hasLinked*`) so
  // it stays correct in the orphan case.
  const blockedPlatformLabel =
    eligibility?.can_link_meta === false ? 'Meta Ads' : 'Google Ads';
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingMeta, setIsSubmittingMeta] = useState(false);
  const anySubmitting = isSubmitting || isSubmittingMeta;

  if (!isOpen) return null;

  const handleLinkAccount = async () => {
    if (!termsAccepted || anySubmitting) return;

    setIsSubmitting(true);

    // Record terms acceptance for account connection
    try {
      await termsApi.acceptTermsForAccountConnection();
      debug.log('Terms acceptance recorded for account connection');
    } catch (error) {
      // Log but don't block - terms were accepted via checkbox
      debug.error('Failed to record terms acceptance for account connection:', error);
    }

    setIsSubmitting(false);
    onClose();

    if (onLinkAccount) {
      // If a callback is provided, use it (e.g., to open the linker on the same page)
      onLinkAccount();
    } else {
      // Otherwise, navigate to dashboard
      navigate('/dashboard');
    }
  };

  const handleConnectMeta = async () => {
    if (!termsAccepted || anySubmitting) return;
    setIsSubmittingMeta(true);
    try {
      await termsApi.acceptTermsForAccountConnection();
    } catch (error) {
      debug.error('Failed to record terms acceptance for Meta connection:', error);
    }
    try {
      const { authorization_url, state } = await metaAdsApi.getAuthorizationUrl();
      window.sessionStorage.setItem(SESSION_KEYS.META_OAUTH_STATE, state);
      // Full-page redirect to Meta's consent screen; the OAuth callback
      // lands at /oauth/callback/meta and resumes the flow there. Do NOT
      // call onClose first — we want the modal to stay visible during the
      // redirect handoff in case the navigation is slow.
      window.location.assign(authorization_url);
    } catch (error) {
      debug.error('Failed to start Meta OAuth flow:', error);
      toast.error("Couldn't start the Meta connection. Please try again.");
      setIsSubmittingMeta(false);
    }
  };

  const modalTitleId = 'link-account-modal-title';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Content */}
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
            <svg
              className="h-8 w-8 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
              />
            </svg>
          </div>
          <h3 id={modalTitleId} className="text-2xl font-bold text-gray-900 mb-2">
            Link Your Ads Account
          </h3>
          <p className="text-gray-600 mb-4">
            To get started with campaign optimization, you'll need to link an ads account first.
          </p>
          <p className="text-sm text-gray-500">
            By connecting your account, you authorize Flouri to access your campaign data for optimization purposes.
          </p>
        </div>

        {/* Terms Checkbox */}
        <div className="mb-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              I agree to the{' '}
              <Link
                to="/terms"
                target="_blank"
                className="text-blue-600 hover:text-blue-800 underline"
                onClick={(e) => e.stopPropagation()}
              >
                Terms & Conditions
              </Link>{' '}
              and{' '}
              <Link
                to="/privacy"
                target="_blank"
                className="text-blue-600 hover:text-blue-800 underline"
                onClick={(e) => e.stopPropagation()}
              >
                Privacy Policy
              </Link>
            </span>
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-2">
          {/* Eligibility loading: show an explicit indicator so an
              unlinked user doesn't stare at an empty modal. Fail-closed
              on the CTAs themselves — we don't render Link buttons until
              the backend confirms eligibility. */}
          {isEligibilityLoading && !eligibility && (
            <div
              className="w-full px-6 py-3 rounded-md border border-gray-200 bg-gray-50 text-sm text-gray-600 inline-flex items-center justify-center gap-2"
              role="status"
              aria-live="polite"
            >
              <svg
                className="w-4 h-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
              Checking plan eligibility…
            </div>
          )}
          {/* Eligibility fetch failed after retries. Render an actionable
              retry state so the user isn't stranded with no explanation. */}
          {isEligibilityError && !eligibility && (
            <div
              className="w-full px-6 py-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-800"
              role="alert"
            >
              <p className="font-medium">Couldn't check plan eligibility.</p>
              <p className="mt-1 text-red-700">
                Please try again in a moment.
              </p>
              <button
                type="button"
                onClick={() => void refetchEligibility()}
                className="mt-2 inline-flex items-center px-3 py-1 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          )}
          {showGoogleButton && (
            <button
              onClick={handleLinkAccount}
              disabled={!termsAccepted || anySubmitting}
              className="w-full px-6 py-3 bg-blue-800 text-white rounded-md hover:bg-blue-900 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium inline-flex items-center justify-center gap-2"
            >
              {isSubmitting ? 'Processing...' : 'Link Google Ads'}
              {!isSubmitting && (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
              )}
            </button>
          )}
          {showMetaButton && (
            <button
              onClick={handleConnectMeta}
              disabled={!termsAccepted || anySubmitting}
              className="w-full px-6 py-3 bg-blue-800 text-white rounded-md hover:bg-blue-900 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium inline-flex items-center justify-center gap-2"
            >
              {isSubmittingMeta ? 'Processing...' : 'Link Meta Ads'}
              {!isSubmittingMeta && (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
              )}
            </button>
          )}
          {showUpgradeNudge && (
            // Light-grey pill with a border — the "Pro" word carries the
            // brand accent (flouri red, bold). Placeholder link target;
            // the Pro branch swaps this for the checkout entry point.
            <Link
              to="/settings?tab=membership"
              onClick={onClose}
              className="w-full px-6 py-3 bg-white border border-gray-300 text-black rounded-md font-medium inline-flex items-center justify-center transition-colors hover:bg-gray-50"
            >
              Upgrade to{' '}
              <span
                className="font-bold mx-1"
                style={{ color: '#cc1a40' }}
              >
                Pro
              </span>{' '}
              to link {blockedPlatformLabel}
            </Link>
          )}
          <button
            onClick={onClose}
            disabled={anySubmitting}
            className="w-full px-6 py-1.5 text-gray-600 hover:text-gray-800 disabled:text-gray-400 font-medium text-sm"
          >
            {showUpgradeNudge ? 'Close' : 'Maybe Later'}
          </button>
        </div>
      </div>
    </div>
  );
}
